import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Typography, Tooltip } from "@mui/material";
import {
  MapPin,
  Users,
  Briefcase,
  ArrowRight,
  Gift,
  Heart,
} from "lucide-react";
import { motion } from "framer-motion";

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const MAX_VISIBLE_BENEFITS = 3;

const CompanyCard = ({ company, onClick, index = 0, enableAnimation = true, isFollowed = false, onFollowToggle }) => {
  const intl = useIntl();
  const cityOrRegion = company.city
    ? company.city
    : company.region
      ? intl.formatMessage({ id: `regions.${company.region}` })
      : null;

  const allBenefits = (company.company_benefits || []).map((key) =>
    intl.formatMessage({ id: `employee_benefits.${key}` })
  );
  const visibleBenefits = allBenefits.slice(0, MAX_VISIBLE_BENEFITS);
  const moreBenefits = allBenefits.length - MAX_VISIBLE_BENEFITS;

  return (
    <Box
      component={enableAnimation ? motion.a : "a"}
      onClick={onClick}
      {...(enableAnimation && {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: index * 0.04, duration: 0.35 },
      })}
      style={{ overflow: "hidden" }}
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        border: 1,
        borderColor: "hsla(160, 10%, 90%, 0.6)",
        borderRadius: "16px",
        p: 2.5,
        cursor: "pointer",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        textDecoration: "none",
        color: "inherit",
        transition: "all 0.3s",
        "&:hover": {
          borderColor: "hsla(153, 68%, 25%, 0.4)",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
        },
        "&:hover .company-name": {
          color: "hsl(153, 68%, 25%)",
        },
        "&:hover .company-arrow": {
          color: "hsl(153, 68%, 25%)",
          transform: "translateX(2px)",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, minWidth: 0 }}>
        {/* Logo */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "12px",
            bgcolor: "hsl(160, 10%, 96%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            flexShrink: 0,
            border: 1,
            borderColor: "hsla(160, 10%, 90%, 0.5)",
            backgroundColor: "hsl(153 68% 25% / 0.1)",
          }}
        >
          {company.logo_url ? (
            <img
              src={company.logo_url}
              srcSet={`${company.logo_url} 1x, ${company.small_logo_url || company.logo_url} 0.5x`}
              alt={`Logo ${company?.name}`}
              loading="lazy"
              style={{ width: 40, height: 40, objectFit: "contain" }}
            />
          ) : (
            <Typography
              sx={{
                fontSize: "1.25rem",
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                color: "hsl(153 68% 25%)",
                userSelect: "none",
              }}
            >
              {company?.name?.charAt(0)?.toUpperCase()}
            </Typography>
          )}
        </Box>

        {/* Name + sector */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            className="company-name"
            sx={{
              fontSize: "1rem",
              lineHeight: 1.2,
              color: "hsl(178, 93%, 10%)",
              transition: "color 0.2s",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              pb: "0.25rem",
            }}
          >
            {company.name}
          </Typography>

          {company.company_industry && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "nowrap", minWidth: 0, maxWidth: "100%" }}>
              <Tooltip
                title={intl.formatMessage({ id: `company.company_industry.${company.company_industry}` })}
                arrow
                disableInteractive
              >
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    px: 1.25,
                    py: 0.25,
                    fontSize: "11px",
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    bgcolor: "hsla(153, 68%, 25%, 0.1)",
                    color: "hsl(153, 68%, 25%)",
                    borderRadius: "9999px",
                    maxWidth: "8rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    flexShrink: 1,
                    minWidth: 0,
                  }}
                >
                  <FormattedMessage id={`company.company_industry.${company.company_industry}`} />
                </Box>
              </Tooltip>
              {company.sub_sector && (
                <Tooltip
                  title={intl.formatMessage({ id: `company.company_sub_sector.${company.sub_sector}` })}
                  arrow
                  disableInteractive
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      px: 1,
                      py: 0.25,
                      fontSize: "10px",
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      bgcolor: "hsl(160, 10%, 96%)",
                      color: "hsl(178, 10%, 45%)",
                      borderRadius: "9999px",
                      maxWidth: "6rem",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <FormattedMessage id={`company.company_sub_sector.${company.sub_sector}`} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Follow / Arrow */}
        {onFollowToggle ? (
          <Box
            component="button"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onFollowToggle(company.id);
            }}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "none", bgcolor: "transparent", cursor: "pointer",
              p: "6px", borderRadius: "8px", flexShrink: 0,
              transition: "all 0.15s", mt: "2px",
            }}
          >
            <Heart
              size={16}
              fill={isFollowed ? "hsl(153, 68%, 25%)" : "none"}
              color={isFollowed ? "hsl(153, 68%, 25%)" : "hsl(178, 10%, 45%)"}
              style={{ transition: "all 0.15s" }}
            />
          </Box>
        ) : (
          <ArrowRight
            size={16}
            className="company-arrow"
            style={{
              color: "hsl(178, 10%, 45%)",
              marginTop: 4,
              flexShrink: 0,
              transition: "all 0.2s",
            }}
          />
        )}
      </Box>

      {/* Description */}
      <Typography
        sx={{
          mt: 1.5,
          fontSize: "0.75rem",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          color: "hsl(178, 10%, 45%)",
          lineHeight: 1.6,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {stripHtml(company.about)}
      </Typography>

      {/* Benefits */}
      {visibleBenefits.length > 0 && (
        <Box
          sx={{
            mt: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
          }}
        >
          {visibleBenefits.map((benefit) => (
            <Box
              key={benefit}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.25,
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                bgcolor: "hsl(72, 93%, 52%, 0.1)",
                color: "hsl(178, 93%, 10%, 0.7)",
                borderRadius: "9999px",
                letterSpacing: "0.02em",
                border: "1px solid hsl(72, 93%, 52%, 0.2)",
              }}
            >
              <Gift size={9} />
              {benefit}
            </Box>
          ))}
          {moreBenefits > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 1,
                py: 0.25,
                fontSize: "10px",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                color: "hsl(178, 10%, 45%)",
                bgcolor: "hsl(160, 10%, 96%)",
                borderRadius: "9999px",
              }}
            >
              +{moreBenefits}
            </Box>
          )}
        </Box>
      )}

      {/* Footer meta */}
      <Box
        sx={{
          mt: "auto",
          pt: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          rowGap: 0.5,
        }}
      >
        {cityOrRegion && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <MapPin size={12} style={{ color: "hsl(178, 10%, 45%)" }} />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "hsl(178, 10%, 45%)",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              }}
            >
              {cityOrRegion}
            </Typography>
          </Box>
        )}

        {company.team_size && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Users size={12} style={{ color: "hsl(178, 10%, 45%)" }} />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "hsl(178, 10%, 45%)",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              }}
            >
              <FormattedMessage id={`company.company_size.options.${company.team_size}`} />
            </Typography>
          </Box>
        )}

        {(company.online_job_offers_count || 0) > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Briefcase size={12} style={{ color: "hsl(153, 68%, 25%)" }} />
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "hsl(153, 68%, 25%)",
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              }}
            >
              {intl.formatMessage(
                { id: "landing_candidate.jobs_count" },
                { jobCount: company.online_job_offers_count }
              )}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CompanyCard;
